import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from '@msi/cobalt-react/button';
import { useNavigate } from 'react-router-dom';
import '@msi/cobalt-react/css/cobalt-light-core.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Icon from '@msi/cobalt-react/icon';
import { Link, useLocation } from 'react-router-dom';
import ApiStructureDropDown from '../components/ApiStructureDropDown/ApiStructureDropDown';
import { Area, Api, ApiAttachment } from '../types/apiData';
import ApiService from '../services/apiService';
import { useOktaAuth } from "@okta/okta-react";
import AreaService from '../services/areaService';
import GovernanceService, { GovernanceEnums, FALLBACK_ENUMS } from '../services/governanceService';
import './add_record.css';
import { Tooltip } from 'bootstrap';
import AppTooltip from '../components/Tooltip/Tooltip';
import appInsights from '../helpers/AppInsights';
import { addToast } from '@msi/cobalt-react/toast';

import DropdownSearchWithInsert from '../components/DropdownSearchWithInsert/DropdownSearchWithInsert';
import { formatDateForInput } from '../helpers/DateHelper';

/**
 * Determines if the given API structure value maps to a valid API Center kind.
 * Mirrors backend mapApiKind logic — only these structures are registered in API Center.
 */
function isApiCenterCompatible(apiStructure: string): boolean {
    if (!apiStructure) return false;
    const lower = apiStructure.toLowerCase().trim();
    return lower.includes("rest") || lower.includes("representational state")
        || lower.includes("graphql")
        || lower.includes("grpc") || lower.includes("google remote procedure")
        || lower.includes("soap") || lower.includes("simple object access")
        || lower.includes("websocket")
        || lower.includes("webhook");
}

interface AddOrEditApiProps {
    alreadyExists?: any;
    onSave?: (fomData: any) => Promise<void>;
}

const AddOrEditApi: React.FC<AddOrEditApiProps> = ({ alreadyExists, onSave }) => {
    const { authState } = useOktaAuth();

    // Memoize service instances so they are not re-created on every render
    const apiService = useMemo(() => new ApiService(), []); 
    const areaService = useMemo(() => new AreaService(), []);
    const governanceService = useMemo(() => new GovernanceService(), []);
    
    const location = useLocation();
    const navigate = useNavigate();
    const apiFromLocation = location.state?.api; // Extract API data from location state
    const shouldDuplicateItem = location.state?.shouldDuplicateItem ?? false; // Check if we should duplicate the item
    const shouldAddVersion = location.state?.shouldAddVersion ?? false; // Check if we should add a version

    const getApiIdFromResponse = (response: any): string => {
        const apiId = response?.id;
        if (typeof apiId !== 'string' || !apiId.trim()) {
            throw new Error('API was created but the response did not include a valid id.');
        }
        return apiId;
    };

    // Initialize with data from either prop or route state
    const initialApiData = alreadyExists || apiFromLocation || null;
    const apiGovernanceOnboardingUrl = process.env.REACT_APP_API_GOVERNANCE_ONBOARDING_URL
        || 'https://motorolasolutions.atlassian.net/wiki/spaces/INTHUB/pages/485872100/For+Product+Teams+API+Onboarding+Governance';
    const showEditGovernanceDisclaimer = !!initialApiData && !shouldDuplicateItem && !shouldAddVersion;

    const [formData, setFormData] = useState({
        lineOfBusiness: '',
        productNameTechnology: '',
        area: '',
        fullApiName: '',
        latestApiVersionNumber: '',
        apiLaunchDate: '',
        apiDescription: '',
        dataType: '',
        apiStructure: '',
        dependencies: '',
        dataSensitivity: '',
        lifecycleStage: '',
        dataOwner: '',
        developerProgram: false,
        technicalControlPoint: '',
        productContactEmail: '',
        technicalContactEmail: '',
        internalApiRepository: '',
        subArea1: '',
        subArea2: '',
        licensingProgramManagerContactEmail: '',
        documentationLink: '',
        apiSpecification: '',
        apiDataPrivacyAssessment: '',
        licenseType: '',
        externalApiRepository: '',
        intellectualPropertyApprovalSystemNumber: '',
        exportControlApprovalLimitations: '',
        attachments: [] as string[],
        apiSpecificationFile: '' as string,
    });

    // Initialize and dispose tooltips properly
    useEffect(() => {
        const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipInstances: any[] = [];

        // Dispose any existing tooltips first
        tooltipElements.forEach((element) => {
            const instance = Tooltip.getInstance(element as Element);
            if (instance) instance.dispose();
        });

        // Initialize all tooltips and track instances
        tooltipElements.forEach((element) => {
            const tooltip = new Tooltip(element as Element);
            tooltipInstances.push(tooltip);
        });

        // Cleanup on unmount or dependency change
        // Use tracked instances instead of re-querying DOM
        return () => {
            tooltipInstances.forEach((instance) => instance.dispose());
        };
    }, [formData.developerProgram, formData.apiStructure]);

    const editApi = !!(alreadyExists || apiFromLocation); // Check if we are in edit mode
    const mapApiDataToForm = (initialApiData: Api) => {
        const lobArea = initialApiData.areas.find((a: any) => a.level === 1);
        const productArea = initialApiData.areas.find((a: any) => a.level === 2);
        const area = initialApiData.areas.find((a: any) => a.level === 3);
        const subArea1 = initialApiData.areas.find((a: any) => a.level === 4);
        const subArea2 = initialApiData.areas.find((a: any) => a.level === 5);

        return {
            lineOfBusiness: lobArea?.name || '',
            productNameTechnology: productArea?.name || '',
            area: area?.name || '',
            subArea1: subArea1?.name || '',
            subArea2: subArea2?.name || '',
            fullApiName: initialApiData.fullApiName || '',
            latestApiVersionNumber: initialApiData.latestApiVersionNumber || '',
            apiLaunchDate: formatDateForInput(initialApiData.apiLaunchDate),
            apiDescription: initialApiData.apiDescription || '',
            dataType: initialApiData.dataType || '',
            apiStructure: initialApiData.apiStructure || '',
            dependencies: initialApiData.dependencies || '',
            dataSensitivity: initialApiData.dataSensitivity || '',
            lifecycleStage: initialApiData.apiCenterMetadata?.lifecycleStage || '',
            dataOwner: initialApiData.dataOwner || '',
            developerProgram: initialApiData.developerProgram || false,
            technicalControlPoint: initialApiData.technicalControlPoint || '',
            productContactEmail: initialApiData.productContactEmail || '',
            technicalContactEmail: initialApiData.technicalContactEmail || '',
            internalApiRepository: initialApiData.internalApiRepository || '',
            licensingProgramManagerContactEmail: initialApiData.licensingProgramManagerContactEmail || '',
            documentationLink: initialApiData.documentationLink || '',
            apiSpecification: typeof initialApiData.apiSpecification === 'string' ? initialApiData.apiSpecification : '',
            apiDataPrivacyAssessment: initialApiData.apiDataPrivacyAssessment || '',
            licenseType: initialApiData.licenseType || '',
            externalApiRepository: initialApiData.externalApiRepository || '',
            intellectualPropertyApprovalSystemNumber: initialApiData.intellectualPropertyApprovalSystemNumber || '',
            exportControlApprovalLimitations: initialApiData.exportControlApprovalLimitations || '',
            attachments: initialApiData.attachments?.map((attachment: any) => attachment?.fileName).filter(Boolean) || [] as string[],
            apiSpecificationFile: typeof initialApiData.apiSpecification === 'object' && initialApiData.apiSpecification ? initialApiData.apiSpecification.fileName || '' : '',
        };
    };

    const [apiNameError, setApiNameError] = useState('');
    const [internalApiRepositoryError, setInternalApiRepositoryError] = useState('');
    const [documentationLinkError, setDocumentationLinkError] = useState('');
    const [apiLaunchDateError, setApiLaunchDateError] = useState('');

    const [dropdownSearch, setDropdownSearch] = useState({
        lineOfBusiness: '',
        productNameTechnology: '',
        area: '',
        subArea1: '',
        subArea2: '',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(!!initialApiData);
    
    const [isRootAreasLoading, setIsRootAreasLoading] = useState(false);
    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const [isAreasLoading, setIsAreasLoading] = useState(false);
    const [isSubAreas1Loading, setIsSubAreas1Loading] = useState(false);
    const [isSubAreas2Loading, setIsSubAreas2Loading] = useState(false);

    const [governanceEnums, setGovernanceEnums] = useState<GovernanceEnums>(FALLBACK_ENUMS);

    const sortAreasByName = useCallback((areas: Area[]): Area[] => {
        return [...areas].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, []);

    const sortedGovernanceOptions = useMemo(() => {
        const sortValues = (values: string[]) => [...values].sort((a, b) => a.localeCompare(b));

        return {
            apiStructures: sortValues(governanceEnums.apiStructures),
            lifecycleStages: sortValues(governanceEnums.lifecycleStages),
            technicalControlPoints: sortValues(governanceEnums.technicalControlPoints),
            dataOwners: sortValues(governanceEnums.dataOwners),
            apiDataPrivacyAssessments: sortValues(governanceEnums.apiDataPrivacyAssessments),
            licenseTypes: sortValues(governanceEnums.licenseTypes),
        };
    }, [governanceEnums]);

    useEffect(() => {
        if (!authState?.isAuthenticated) return;
        governanceService.getEnums(authState).then(setGovernanceEnums).catch(() => {});
    }, [authState, governanceService]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                if (initialApiData) {
                    setIsEditing(true); 

                    // Scenario 1: initialApiData is considered complete (e.g., from navigation state)
                    // We add a more robust check: initialApiData.fullApiName and that areas is an array.
                    if (initialApiData.fullApiName && Array.isArray(initialApiData.areas)) {
                        const mappedData = mapApiDataToForm(initialApiData);
                        
                        // Clear version-specific fields if adding a version
                        if (shouldAddVersion) {
                            mappedData.latestApiVersionNumber = '';
                            mappedData.apiSpecificationFile = '';
                            // Clear attachments list from form so parent's files don't carry over
                            mappedData.attachments = [];
                        }
                        
                        // Preserve existing attachment objects (with URLs) — only for editing, not new versions
                        if (!shouldAddVersion && initialApiData.attachments && Array.isArray(initialApiData.attachments)) {
                            const validAttachments = initialApiData.attachments.filter(
                                (a: any) => a && typeof a === 'object' && a.url
                            );
                            setExistingAttachments(validAttachments);
                        }
                        // Preserve existing apiSpecification object — only for editing, not new versions
                        if (!shouldAddVersion && initialApiData.apiSpecification && typeof initialApiData.apiSpecification === 'object' && initialApiData.apiSpecification.url) {
                            setExistingApiSpecification(initialApiData.apiSpecification);
                        }

                        // Set general form data, including the formatted apiLaunchDate
                        setFormData(mappedData);

                        // Set area IDs in formData using the original initialApiData.areas
                        // This assumes initialApiData.areas is correct and complete in this branch
                        setFormData((prev) => ({
                            ...prev,
                            lineOfBusiness: initialApiData.areas.find((a: any) => a.level === 1)?.id || '',
                            productNameTechnology: initialApiData.areas.find((a: any) => a.level === 2)?.id || '',
                            area: initialApiData.areas.find((a: any) => a.level === 3)?.id || '',
                            subArea1: initialApiData.areas.find((a: any) => a.level === 4)?.id || '',
                            subArea2: initialApiData.areas.find((a: any) => a.level === 5)?.id || '',
                        }));

                        // Set dropdown search display names
                        setDropdownSearch({
                            lineOfBusiness: mappedData.lineOfBusiness,
                            productNameTechnology: mappedData.productNameTechnology,
                            area: mappedData.area,
                            subArea1: mappedData.subArea1,
                            subArea2: mappedData.subArea2,
                        });

                    // Scenario 2: initialApiData is partial (e.g., just an ID from 'alreadyExists' prop), so fetch full data
                    } else if (initialApiData.id) {                        
                        try {
                        const apiDataItem = await apiService.getApiById(initialApiData.id, authState);                        
                            const mappedData = mapApiDataToForm(apiDataItem);
                            
                            // Clear version-specific fields if adding a version
                            if (shouldAddVersion) {
                                mappedData.latestApiVersionNumber = '';
                                mappedData.apiSpecificationFile = '';
                                mappedData.attachments = [];
                            }
                            
                            // Preserve existing attachment objects (with URLs) — only for editing, not new versions
                            if (!shouldAddVersion && apiDataItem.attachments && Array.isArray(apiDataItem.attachments)) {
                                const validAttachments = apiDataItem.attachments.filter(
                                    (a: any) => a && typeof a === 'object' && a.url
                                );
                                setExistingAttachments(validAttachments);
                            }
                            // Preserve existing apiSpecification object — only for editing, not new versions
                            if (!shouldAddVersion && apiDataItem.apiSpecification && typeof apiDataItem.apiSpecification === 'object' && (apiDataItem.apiSpecification as any).url) {
                                setExistingApiSpecification(apiDataItem.apiSpecification);
                            }

                            // Set general form data, including the formatted apiLaunchDate
                            setFormData(mappedData);

                            // Set area IDs in formData using the *fetched* apiDataItem.areas
                            setFormData((prev) => ({
                                ...prev,
                                lineOfBusiness: apiDataItem.areas.find((a: any) => a.level === 1)?.id || '',
                                productNameTechnology: apiDataItem.areas.find((a: any) => a.level === 2)?.id || '',
                                area: apiDataItem.areas.find((a: any) => a.level === 3)?.id || '',
                                subArea1: apiDataItem.areas.find((a: any) => a.level === 4)?.id || '',
                                subArea2: apiDataItem.areas.find((a: any) => a.level === 5)?.id || '',
                            }));

                            // Set dropdown search display names
                            setDropdownSearch({
                                lineOfBusiness: mappedData.lineOfBusiness,
                                productNameTechnology: mappedData.productNameTechnology,
                                area: mappedData.area,
                                subArea1: mappedData.subArea1,
                                subArea2: mappedData.subArea2,
                            });
                        } catch (error) {
                            console.error(`API with ID ${initialApiData.id} not found.`);                            
                        }
                    } else {
                        // initialApiData exists but is not in a recognized "complete" or "partial (ID-only)" state
                        console.warn("Initial API data is present but in an unexpected format:", initialApiData);
                        setIsEditing(false); // Treat as a new form
                    }
                } else {
                    // No initialApiData, so it's a new record form
                    setIsEditing(false);
                    // Optionally, explicitly reset formData and dropdownSearch to their default empty states
                    // This is good practice if the component might be re-used without unmounting
                    setFormData({
                        lineOfBusiness: '', productNameTechnology: '', area: '', fullApiName: '',
                        latestApiVersionNumber: '', apiLaunchDate: '', apiDescription: '', dataType: '',
                        apiStructure: '', dependencies: '', dataSensitivity: '', lifecycleStage: '', dataOwner: '',
                        developerProgram: false, technicalControlPoint: '', productContactEmail: '',
                        technicalContactEmail: '', internalApiRepository: '', subArea1: '', subArea2: '',
                        licensingProgramManagerContactEmail: '', documentationLink: '', apiSpecification: '',
                        apiDataPrivacyAssessment: '', licenseType: '', externalApiRepository: '',
                        intellectualPropertyApprovalSystemNumber: '', exportControlApprovalLimitations: '', attachments: [] as string[],
                        apiSpecificationFile: '' as string,
                    });
                    setDropdownSearch({ lineOfBusiness: '', productNameTechnology: '', area: '', subArea1: '', subArea2: '' });
                }
            } catch (error) {
                console.error("Error processing initial data:", error);                
            }
        };

        fetchInitialData();
    }, [initialApiData, authState, apiService]); // Dependencies: initialApiData, authState, apiService

    const getLineOfBusiness = useCallback(async (): Promise<Area[]> => {
        var lineOfBusiness: Area[] = [];
        try {
            setIsRootAreasLoading(true);
            lineOfBusiness = sortAreasByName(await areaService.getRootAreas(authState) as Area[]);
        }
        catch (error) {
            console.error("Error fetching line of business:", error);
        }
        finally {
            setIsRootAreasLoading(false);
        }
        return lineOfBusiness;
    }, [authState, sortAreasByName]);

    const getProducts = useCallback(async (): Promise<Area[]> => {
        var products: Area[] = [];
        if (formData.lineOfBusiness) {
            try {
                setIsProductsLoading(true);
                products = sortAreasByName(await areaService.getAreasByParentId(formData.lineOfBusiness, authState) as Area[]);
            }
            catch (error) {
                console.error("Error fetching products:", error);
            }
            finally {
                setIsProductsLoading(false);
            }
        }
        return products;
    }, [formData.lineOfBusiness, authState, sortAreasByName]);

    const getAreas = useCallback(async (): Promise<Area[]> => {
        var areas: Area[] = [];
        if (formData.productNameTechnology) {
            try {
                setIsAreasLoading(true);
                areas = sortAreasByName(await areaService.getAreasByParentId(formData.productNameTechnology, authState) as Area[]);
            }
            catch (error) {
                console.error("Error fetching areas:", error);
            }
            finally {
                setIsAreasLoading(false);
            }
        }
        return areas;
    }, [formData.productNameTechnology, authState, sortAreasByName]);

    const getSubArea1 = useCallback(async (): Promise<Area[]> => {
        var subAreas1: Area[] = [];
        if (formData.area) {
            try {
                setIsSubAreas1Loading(true);
                subAreas1 = sortAreasByName(await areaService.getAreasByParentId(formData.area, authState) as Area[]);
            }
            catch (error) {
                console.error("Error fetching subAreas1:", error);
            }
            finally {
                setIsSubAreas1Loading(false);
            }
        }
        return subAreas1;
    }, [formData.area, authState, sortAreasByName]);

    const getSubArea2 = useCallback(async (): Promise<Area[]> => {
        var subAreas2: Area[] = [];
        if (formData.subArea1) {
            try {
                setIsSubAreas2Loading(true);
                subAreas2 = sortAreasByName(await areaService.getAreasByParentId(formData.subArea1, authState) as Area[]);
            }
            catch (error) {
                console.error("Error fetching subAreas2:", error);
            }
            finally {
                setIsSubAreas2Loading(false);
            }
        }
        return subAreas2;
    }, [formData.subArea1, authState, sortAreasByName]);

    const handleDropdownChange = (name: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleLineOfBusinessChange = (value: string) => {
        setDropdownSearch({ ...dropdownSearch, lineOfBusiness: value });
        
        setDropdownSearch((prev) => ({ 
            ...prev, 
            productNameTechnology: '',
            area: '',
            subArea1: '',
            subArea2: '',
        })); 
        setFormData((prev) => ({ 
            ...prev, 
            productNameTechnology: '',
            area: '',
            subArea1: '',
            subArea2: '',
        }));
    }

    const handleProductNameTechnologyChange = (value: string) => {
        setDropdownSearch({ ...dropdownSearch, productNameTechnology: value });
        
        setDropdownSearch((prev) => ({ 
            ...prev, 
            area: '',
            subArea1: '',
            subArea2: '',
        })); 
        setFormData((prev) => ({ 
            ...prev, 
            area: '',
            subArea1: '',
            subArea2: '',
        }));
    }

    const handleAreaChange = (value: string) => {
        setDropdownSearch({ ...dropdownSearch, area: value });
        
        setDropdownSearch((prev) => ({ 
            ...prev, 
            subArea1: '',
            subArea2: '',
        })); 
        setFormData((prev) => ({ 
            ...prev, 
            subArea1: '',
            subArea2: '',
        }));
    }

    const handleSubArea1Change = (value: string) => {
        setDropdownSearch({ ...dropdownSearch, subArea1: value });
        
        setDropdownSearch((prev) => ({ 
            ...prev, 
            subArea2: '',
        })); 
        setFormData((prev) => ({ 
            ...prev, 
            subArea2: '',
        }));
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        if (name === 'fullApiName') {
            if (/[()]/.test(value)) {
                setApiNameError('API Name cannot contain parentheses "(" or ")".');
            } else {
                setApiNameError('');
            }
        }

        if (name === 'internalApiRepository') {
            try {
                // Try to construct a URL object to validate
                new URL(value);
                setInternalApiRepositoryError('');
            } catch {
                setInternalApiRepositoryError('Please enter a valid URL (e.g. https://www.example.com).');
            }
        }

        if (name === 'documentationLink') {
            if (value.trim() === '') {
                setDocumentationLinkError('');
            } else {
                try {
                    new URL(value);
                    setDocumentationLinkError('');
                } catch {
                    setDocumentationLinkError('Please enter a valid URL (e.g. https://www.example.com).');
                }
            }
        }

        if (name === 'apiLaunchDate') {
            if (value.trim() === '') {
                setApiLaunchDateError('');
            } else {
                const selectedDate = new Date(value);
                const currentDate = new Date();
                const minAllowedDate = new Date('1900-01-01');
                const maxAllowedDate = new Date();
                maxAllowedDate.setFullYear(currentDate.getFullYear() + 1);
                
                if (selectedDate < minAllowedDate) {
                    setApiLaunchDateError('API Launch Date must be on or after January 1, 1900.');
                } else if (selectedDate > maxAllowedDate) {
                    const maxDateString = maxAllowedDate.toLocaleDateString();
                    setApiLaunchDateError(`API Launch Date cannot be later than ${maxDateString} (365 days from today).`);
                } else {
                    setApiLaunchDateError('');
                }
            }
        }

        setFormData({ ...formData, [name]: fieldValue });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (apiNameError || internalApiRepositoryError || documentationLinkError || apiLaunchDateError) {
            return; // Prevent submission if there's an error
        }

        setIsLoading(true);

        const submissionData = {
            ...formData,
            lineOfBusiness: dropdownSearch.lineOfBusiness,
            productNameTechnology: dropdownSearch.productNameTechnology,
            area: dropdownSearch.area,
            subArea1: dropdownSearch.subArea1,
            subArea2: dropdownSearch.subArea2,
            attachments: [
                // Preserve existing attachment objects (with URLs) that are still in the form
                ...existingAttachments.filter(a => formData.attachments.includes(a.fileName)),
                // New files will be uploaded separately and linked via handleFileUpload
                ...selectedFiles.map(file => file.name),
            ],
            // Preserve existing apiSpecification object if no new spec file was selected
            ...(existingApiSpecification && !selectedApiSpecFile
                ? { apiSpecification: existingApiSpecification }
                : {}),
        };

        try {
            if (shouldDuplicateItem) {
                // Duplicate Api
                const response = await apiService.addAPI(submissionData, authState);
                const createdApiId = getApiIdFromResponse(response);

                appInsights.trackEvent({ 
                    name: 'API Duplicated', 
                    properties: { apiName: formData.fullApiName, apiId: createdApiId } 
                });

                console.log('API added successfully:', response);
                addToast('success', 'API added successfully!', { containerId: 'default' });

                await handleFileUpload(submissionData.attachments, createdApiId);
                
                // Call onSave if it exists
                if (onSave) {
                    await onSave(submissionData);
                }
                
                handleClearForm();
            } else if (shouldAddVersion) {
                // Add new version
                if (!initialApiData || !initialApiData.id) {
                    throw new Error("Cannot create version: Missing parent API ID");
                }

                const response = await apiService.createApiVersion(initialApiData.id, submissionData, authState);

                appInsights.trackEvent({ 
                    name: 'API Version Created', 
                    properties: { 
                        apiName: formData.fullApiName, 
                        versionNumber: formData.latestApiVersionNumber,
                        parentApiId: initialApiData.id,
                        versionId: (response as any).id 
                    } 
                });

                console.log('API version created successfully:', response);
                addToast('success', `API version ${formData.latestApiVersionNumber} created successfully!`, { containerId: 'default' });

                await handleFileUpload(submissionData.attachments, (response as any).id);
                
                // Call onSave if it exists
                if (onSave) {
                    await onSave(submissionData);
                }
                
                // Navigate to the parent API details page
                navigate(`/api-details/${initialApiData.id}`);
            } else if (editApi) {
                // Make sure we have an ID for the API we're editing
                if (!initialApiData || !initialApiData.id) {
                    throw new Error("Cannot edit API: Missing API ID");
                }
                
                // Pass the formData to the editAPI function
                const updatedApi = await apiService.editAPI(initialApiData.id, submissionData, authState);

                appInsights.trackEvent({ 
                    name: 'API Edited', 
                    properties: { apiName: formData.fullApiName, apiId: initialApiData.id } 
                });

                console.log('API edited successfully:', updatedApi);
                addToast('success', 'API updated successfully!', { containerId: 'default' });

                await handleFileUpload(submissionData.attachments, initialApiData.id);

                // Call onSave if it exists
                if (onSave) {
                    await onSave(submissionData);
                }

                // Navigate back to table view after successful edit
                navigate('/table-view');
            } else {
                // Add new API
                const response = await apiService.addAPI(submissionData, authState);
                const createdApiId = getApiIdFromResponse(response);

                appInsights.trackEvent({ 
                    name: 'API Added', 
                    properties: { apiName: formData.fullApiName, apiId: createdApiId } 
                });

                console.log('API added successfully:', response);
                addToast('success', 'API added successfully!', { containerId: 'default' });

                await handleFileUpload(submissionData.attachments, createdApiId);
                
                // Call onSave if it exists
                if (onSave) {
                    await onSave(submissionData);
                }

                // Redirect to the newly created API details page.
                navigate(`/api-details/${createdApiId}`);
            }
        } catch (error: any) {
            appInsights.trackException({ error });
            if (error.response && error.response.data) {
                addToast('error', error.response.data.error || error.response.data.message, { containerId: 'default' });
            } else {
                addToast('error', 'An unexpected error occurred.', { containerId: 'default' });
            }
            console.error('Error with API operation:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEditing = () => {
        navigate('/table-view');
    }

    const handleClearForm = () => {
        setFormData({
            lineOfBusiness: '',
            productNameTechnology: '',
            area: '',
            fullApiName: '',
            latestApiVersionNumber: '',
            apiLaunchDate: '',
            apiDescription: '',
            dataType: '',
            apiStructure: '',
            dependencies: '',
            dataSensitivity: '',
            lifecycleStage: '',
            dataOwner: '',
            developerProgram: false,
            technicalControlPoint: '',
            productContactEmail: '',
            technicalContactEmail: '',
            internalApiRepository: '',
            subArea1: '',
            subArea2: '',
            licensingProgramManagerContactEmail: '',
            documentationLink: '',
            apiSpecification: '',
            apiDataPrivacyAssessment: '',
            licenseType: '',
            externalApiRepository: '',
            intellectualPropertyApprovalSystemNumber: '',
            exportControlApprovalLimitations: '',
            attachments: [] as string[],
            apiSpecificationFile: '' as string,
        });
        
        // Also clear the dropdown search values
        setDropdownSearch({
            lineOfBusiness: '',
            productNameTechnology: '',
            area: '',
            subArea1: '',
            subArea2: '',
        });
        
        // Clear selected files
        setSelectedFiles([]);
        setSelectedApiSpecFile(null);
        
        // Clear any validation errors
        setApiNameError('');
        setInternalApiRepositoryError('');
        setDocumentationLinkError('');
        setApiLaunchDateError('');
    };

    // File Upload
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedApiSpecFile, setSelectedApiSpecFile] = useState<File | null>(null);
    // Track existing attachment objects (with URLs) from parent/previous versions
    const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
    const [existingApiSpecification, setExistingApiSpecification] = useState<any>(null);
    
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const allowedExtensions = [
                '.pdf', '.mp4', '.mp3', '.mov', '.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff',
                '.csv', '.txt', '.log', '.tsv', '.js', '.json', '.py', '.cpp', '.java', '.html',
                '.css', '.ts', '.c', '.scss', '.htm', '.xml', '.md', '.sh', '.sql', '.php', '.rb',
                '.pl', '.go', '.swift', '.kt'
            ];

            const files = Array.from(event.target.files).filter(file => {
                const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                if (!allowedExtensions.includes(fileExtension)) {
                    addToast('error', `Invalid file extension: ${file.name}`, { containerId: 'default' });
                }
                return allowedExtensions.includes(fileExtension);
            });

            // Filter out files that are already selected to prevent duplicates
            const newFiles = files.filter(newFile => 
                !selectedFiles.some(existingFile => 
                    existingFile.name === newFile.name && existingFile.size === newFile.size
                )
            );

            // Append new files to existing selected files instead of replacing them
            setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
            
            // Clear the input value to allow selecting the same file again if needed
            event.target.value = '';
        }
    };

    const handleApiSpecFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            const allowedExtensions = ['.json', '.yaml', '.yml', '.xml', '.wsdl'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            
            if (!allowedExtensions.includes(fileExtension)) {
                addToast('error', `Invalid file extension for API specification: ${file.name}. Allowed: ${allowedExtensions.join(', ')}`, { containerId: 'default' });
                event.target.value = '';
                return;
            }
            
            setSelectedApiSpecFile(file);
            setFormData(prev => ({
                ...prev,
                apiSpecificationFile: file.name
            }));
            
            // Clear the input value to allow selecting a different file
            event.target.value = '';
        }
    };

    const handleFileUpload = async (attachments: string[], apiId: string) => {
        // Upload regular attachments
        if (selectedFiles.length > 0 && apiId) {
            try {
                const formData = new FormData();
                selectedFiles.forEach((file, index) => {
                    formData.append('files', file);
                });
                formData.append('apiId', apiId);
                const response = await apiService.addAttachments(formData, authState);
                console.log('Attachments uploaded successfully:', response);
            } catch (error) {
                console.error('Error uploading attachments:', error);
                addToast('error', 'An error occurred while uploading attachments.', { containerId: 'default' });
            }
        }

        // Upload API specification separately
        if (selectedApiSpecFile && apiId) {
            try {
                const specFormData = new FormData();
                specFormData.append('file', selectedApiSpecFile);
                specFormData.append('apiId', apiId);
                const response = await apiService.addApiSpecification(specFormData, authState);
                console.log('API specification uploaded successfully:', response);
            } catch (error) {
                console.error('Error uploading API specification:', error);
                addToast('error', 'An error occurred while uploading API specification.', { containerId: 'default' });
            }
        }
    }

    return (
        <div className="mt-1">
            <div style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                <form onSubmit={handleSubmit}>
                    {/* Header Section */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex">
                            <Link
                                to="/table-view"
                                className={`menu-link ${location.pathname === '/table-view' ? 'active' : ''}`}
                            >
                                <Icon
                                    name="ic_back"
                                    size={36}
                                    color="primary"
                                    className="me-2"
                                    style={{ cursor: 'pointer' }}
                                />
                            </Link>
                            <Icon name="ic_data_table" size={36} color="primary" className="me-2" />
                            <h2 className="mb-0">
                                {shouldDuplicateItem
                                    ? `Duplicate API: ${formData.fullApiName}`
                                    : shouldAddVersion
                                    ? `Add Version: ${formData.fullApiName}`
                                    : isEditing
                                    ? "Edit API"
                                    : "Add Single API"}
                            </h2>
                        </div>
                        <div className="d-flex">
                            {isEditing || shouldAddVersion ? (
                                <Button
                                    text="Cancel"
                                    buttonType="secondary"
                                    className="me-2"
                                    onClick={handleCancelEditing}
                                />
                            ) : (
                                <Button
                                    text="Clear"
                                    buttonType="secondary"
                                    className="me-2"
                                    onClick={handleClearForm}
                                />
                            )}
                            <Button
                                type="submit"
                                text={isLoading ? "Submitting..." : shouldDuplicateItem ? "Duplicate" : shouldAddVersion ? "Add Version" : isEditing ? "Save Changes" : "Add API to Hub"}
                                buttonType="primary"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    {/* API Governance Disclaimer */}
                    <div className="alert alert-warning d-flex align-items-start" role="alert">
                        <div className='ms-1'>
                            <Icon
                                name="ic_information"
                                size={25}
                                color="#856404"
                                className="msi-icon"
                            />
                        </div>
                        <div className='ms-2 text-start flex-grow-1'>
                            <p className="mb-0">
                                <strong>Note:</strong>{' '}
                                {showEditGovernanceDisclaimer ? (
                                    <>
                                        Changes made here will update API Center but will <strong>not</strong> be synced back to your project's GitHub repository. To maintain a single source of truth, we recommend making changes directly to your OpenAPI spec. Read more in the{' '}
                                    </>
                                ) : (
                                    <>
                                        Creating an API here registers it in API Center but bypasses the API Governance pipeline, including mandatory quality and security checks. It does not create a GitHub repository or OpenAPI file. For full technical governance, follow the{' '}
                                    </>
                                )}
                                <a
                                    href={apiGovernanceOnboardingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="alert-link link-underline link-underline-opacity-0"
                                >
                                    Developer Onboarding Guide
                                </a>.
                            </p>
                        </div>
                    </div>

                    {/* Callout Section */}
                    <div className="alert alert-primary d-flex alert-dismissible fade show" role="alert">
                        <div className='ms-1'>
                            <Icon
                                name="ic_information"
                                size={25}
                                color="#007aaa"
                                className="msi-icon"
                            />
                        </div>
                        <div className='ms-2 text-start flex-grow-1'>
                            <p>
                                For easy adding, please check out quick guide on how to fill out the form below. It will walk you through every step. 
                            </p>
                            <a  
                                href="/guides/Guide to Adding or Editing an API Record.pdf"
                                target='blank'
                                download 
                                className="alert-link link-underline link-underline-opacity-0">Read the Guide</a>
                        </div>
                        <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                    </div>

                    {/* Basic Information Section */}
                    <div className="card mb-4 rounded-1">
                        <h5 className="card-title m-3 mb-0"> Basic Information </h5>
                        <div className="card-body pt-0">
                            <div className="row">
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="fullApiName" className="form-label">
                                        API Name <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="text"
                                            id="fullApiName"
                                            name="fullApiName"
                                            className={`form-control ${apiNameError ? 'is-invalid' : ''}`}
                                            value={formData.fullApiName}
                                            onChange={handleChange}
                                            required
                                            disabled={shouldAddVersion}
                                        />
                                        {apiNameError && <div className="invalid-feedback">{apiNameError}</div>}
                                        <AppTooltip
                                            title='The complete and unique official name of the API.'
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="latestApiVersionNumber" className="form-label">
                                        Version Number <span className="text-danger">*</span> (reference: <a href="https://semver.org" target="_blank" rel="noopener noreferrer">https://semver.org</a>)
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="text"
                                            id="latestApiVersionNumber"
                                            name="latestApiVersionNumber"
                                            className="form-control"
                                            value={formData.latestApiVersionNumber}
                                            onChange={handleChange}
                                            required
                                        />
                                        <AppTooltip
                                            title='The current version identifier for the API.'
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="apiLaunchDate" className="form-label">
                                        API Launch Date
                                    </label>
                                    <div className='position-relative input-group'>
                                        <input
                                            type="date"
                                            id="apiLaunchDate"
                                            name="apiLaunchDate"
                                            className={`form-control ${apiLaunchDateError ? 'is-invalid' : ''}`}
                                            value={formData.apiLaunchDate}
                                            onChange={handleChange}
                                        />
                                        {apiLaunchDateError && <div className="invalid-feedback">{apiLaunchDateError}</div>}
                                        <AppTooltip
                                            title='The date when this version of the API was (or is planned to be) officially launched and made available.'
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="apiDescription" className="form-label">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <div className='position-relative input-group'>
                                        <textarea
                                            id="apiDescription"
                                            name="apiDescription"
                                            className="form-control"
                                            value={formData.apiDescription}
                                            onChange={handleChange}
                                            required
                                            rows={8}
                                        />
                                        <AppTooltip
                                            title="A concise summary of the API's purpose, functionality, and what it enables users or systems to do."
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-8">
                                    <div className="row">
                                        <div className="col-md-6 mt-4">
                                            <DropdownSearchWithInsert 
                                                createAreaHeaderLabel="Add New Line of Business"
                                                parentAreaId={null}
                                                isRootArea={true}
                                                dropdownId="lineOfBusiness"
                                                dropdownLabel="Line of Business"
                                                dropdownPlaceholder="Search Line of Business"
                                                dropdownValue={dropdownSearch.lineOfBusiness}
                                                dropdownRequired={true}
                                                dropdownToolTip="The highest-level business unit or segment the API belongs to. Selecting a Line of Business will populate the &quot;Product Name/Technology&quot; dropdown."
                                                dropdownGetOptions={getLineOfBusiness}
                                                dropdownOnSearchChange={handleLineOfBusinessChange}
                                                dropdownOnOptionSelect={(id, name) => {
                                                    handleDropdownChange('lineOfBusiness', id);
                                                    setDropdownSearch({ ...dropdownSearch, lineOfBusiness: name });
                                                }}
                                                dropdownDisabled={shouldAddVersion}
                                            />
                                        </div>
                                        <div className="col-md-6 mt-4">
                                            <DropdownSearchWithInsert 
                                                createAreaHeaderLabel="Add New Product Name/Technology"
                                                parentAreaId={formData.lineOfBusiness}
                                                isRootArea={false}
                                                dropdownId="productNameTechnology"
                                                dropdownLabel="Product Name/Technology"
                                                dropdownPlaceholder="Search Product Name/Technology"
                                                dropdownValue={dropdownSearch.productNameTechnology}
                                                dropdownRequired={true}
                                                dropdownToolTip='The specific product, product line, or technology within the selected Line of Business that the API is associated with. Selecting a Product will populate the "Area" dropdown.&#10;Format: Select from a searchable dropdown list. Options are filtered based on the selected "Line of Business".'
                                                dropdownGetOptions={getProducts}
                                                dropdownOnSearchChange={handleProductNameTechnologyChange}
                                                dropdownOnOptionSelect={(id, name) => {
                                                    handleDropdownChange('productNameTechnology', id);
                                                    setDropdownSearch({ ...dropdownSearch, productNameTechnology: name });
                                                }}
                                                dropdownDisabled={shouldAddVersion}
                                            />
                                        </div>
                                        <div className="col-md-6 mt-4">
                                            <DropdownSearchWithInsert 
                                                createAreaHeaderLabel="Add New Area"
                                                parentAreaId={formData.productNameTechnology}
                                                isRootArea={false}
                                                dropdownId="area"
                                                dropdownLabel="Area"
                                                dropdownPlaceholder="Search Area"
                                                dropdownValue={dropdownSearch.area}
                                                dropdownRequired={true}
                                                dropdownToolTip='A more specific functional or domain area within the selected Product. Selecting an Area can populate "Sub Area 1".'
                                                dropdownGetOptions={getAreas}
                                                dropdownOnSearchChange={handleAreaChange}
                                                dropdownOnOptionSelect={(id, name) => {
                                                    handleDropdownChange('area', id);
                                                    setDropdownSearch({ ...dropdownSearch, area: name });
                                                }}
                                                dropdownDisabled={shouldAddVersion}
                                            />
                                        </div>
                                        <div className="col-md-6 mt-4">
                                            <DropdownSearchWithInsert 
                                                createAreaHeaderLabel="Add New Sub Area 1"
                                                parentAreaId={formData.area}
                                                isRootArea={false}
                                                dropdownId="subArea1"
                                                dropdownLabel="Sub Area 1"
                                                dropdownPlaceholder="Search Sub Area 1"
                                                dropdownValue={dropdownSearch.subArea1}
                                                dropdownRequired={false}
                                                dropdownToolTip='An optional, more granular sub-division under the selected "Area". Selecting a Sub Area 1 can populate "Sub Area 2".'
                                                dropdownGetOptions={getSubArea1}
                                                dropdownOnSearchChange={handleSubArea1Change}
                                                dropdownOnOptionSelect={(id, name) => {
                                                    handleDropdownChange('subArea1', id);
                                                    setDropdownSearch({ ...dropdownSearch, subArea1: name });
                                                }}
                                                dropdownDisabled={shouldAddVersion}
                                            />
                                        </div>
                                        <div className="col-md-6 mt-4">
                                            <DropdownSearchWithInsert 
                                                createAreaHeaderLabel="Add New Sub Area 2"
                                                parentAreaId={formData.subArea1}
                                                isRootArea={false}
                                                dropdownId="subArea2"
                                                dropdownLabel="Sub Area 2"
                                                dropdownPlaceholder="Search Sub Area 2"
                                                dropdownValue={dropdownSearch.subArea2}
                                                dropdownRequired={false}
                                                dropdownToolTip='An optional, further sub-division under the selected "Sub Area 1".'
                                                dropdownGetOptions={getSubArea2}
                                                dropdownOnSearchChange={(value) => setDropdownSearch({ ...dropdownSearch, subArea2: value })}
                                                dropdownOnOptionSelect={(id, name) => {
                                                    handleDropdownChange('subArea2', id);
                                                    setDropdownSearch({ ...dropdownSearch, subArea2: name });
                                                }}
                                                dropdownDisabled={shouldAddVersion}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Section*/}
                    <div className="card mb-4 rounded-1">
                        <h5 className="card-title m-3 mb-0"> Data</h5>
                        <div className="card-body pt-0">
                            <div className="row">
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="dataType" className="form-label">
                                        Data Type <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                    <input
                                        type="text"
                                        id="dataType"
                                        name="dataType"
                                        className="form-control"
                                        value={formData.dataType}
                                        onChange={handleChange}
                                        required
                                    />
                                    <AppTooltip
                                        title='Describes the general category or type of data the API processes or exposes.'
                                        placement="bottom"
                                    />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="dataSensitivity" className="form-label">
                                        Data Sensitivity <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <select
                                            id="dataSensitivity"
                                            name="dataSensitivity"
                                            className="form-select"
                                            value={formData.dataSensitivity}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select</option>
                                            {governanceEnums.dataSensitivity.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <AppTooltip
                                            title='The classification level of the data handled by the API, based on its confidentiality and impact if compromised.'
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <ApiStructureDropDown
                                        value={formData.apiStructure}
                                        onChange={(e) => handleChange(e)}
                                        required
                                        baseOptions={sortedGovernanceOptions.apiStructures}
                                    />
                                </div>
                                {((isEditing && initialApiData?.apiCenterMetadata) || (!isEditing && isApiCenterCompatible(formData.apiStructure))) && (
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="lifecycleStage" className="form-label">
                                        Lifecycle Stage <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <select
                                            id="lifecycleStage"
                                            name="lifecycleStage"
                                            className="form-select"
                                            value={formData.lifecycleStage}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select</option>
                                            {sortedGovernanceOptions.lifecycleStages.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <AppTooltip
                                            title='The current lifecycle stage of the API version. Drives Azure API Center lifecycle tracking and API Hub display.'
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                )}
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="technicalControlPoint" className="form-label">
                                        Technical Control Point <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <select
                                            id="technicalControlPoint"
                                            name="technicalControlPoint"
                                            className="form-select"
                                            value={formData.technicalControlPoint}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select</option>
                                            {sortedGovernanceOptions.technicalControlPoints.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <AppTooltip
                                            title='The primary security mechanism or technology used to control access to the API.'
                                            placement="bottom"/>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                    <label htmlFor="dataOwner" className="form-label">
                                        Data Owner <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <select
                                            id="dataOwner"
                                            name="dataOwner"
                                            className="form-select"
                                            value={formData.dataOwner}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select</option>
                                            {sortedGovernanceOptions.dataOwners.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <AppTooltip
                                            title='The entity or group primarily responsible for the data that the API manages or exposes.'
                                            placement="bottom"
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 mt-4">
                                        <label htmlFor="dataOwner" className="form-label">
                                        Add Attachments
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="file"
                                            multiple
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            id="fileInput"
                                        />
                                        <Button
                                            text="Add Attachments"
                                            buttonType="secondary"
                                            className="me-2"
                                            onClick={() => document.getElementById('fileInput')?.click()}
                                        />
                                        <span
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="bottom"
                                            data-bs-custom-class="custom-tooltip"
                                            data-bs-title='Additional files or documents related to the API.'
                                            style={{ cursor: "pointer" }}
                                        >
                                            <Icon
                                                name="ic_information"
                                                size={18}
                                                color="#777"
                                                className="msi-icon"
                                            />
                                        </span>
                                        {formData.attachments.length > 0 && (
                                            <div className="row mt-3">
                                                <div className="col-lg-12">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {formData.attachments.map((fileName, index) => (
                                                            <div
                                                                key={index}
                                                                className="d-flex align-items-center"
                                                                style={{ padding: '0.2rem', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                                                            >
                                                                <span className="me-2">{fileName}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {selectedFiles.length > 0 && (
                                            <div className="row mt-3">
                                                <div className="col-lg-12">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {selectedFiles.map((file, index) => (
                                                            <div
                                                                key={index}
                                                                className="d-flex align-items-center"
                                                                style={{ padding: '0.2rem', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                                                            >
                                                                <span className="me-2">{file.name}</span>
                                                                <span
                                                                    onClick={() => {
                                                                        setSelectedFiles((prevFiles) =>
                                                                            prevFiles.filter((_, i) => i !== index)
                                                                        );
                                                                    }}
                                                                    style={{ cursor: 'pointer', color: 'red' }}
                                                                >
                                                                    <Icon
                                                                        name="ic_trash"
                                                                        size={18}
                                                                        color="red"
                                                                    />
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-xl-3 col-lg-4 col-md-6 mt-4">
                                    <div className="form-check">
                                        <input
                                            id="developerProgram"
                                            name="developerProgram"
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={formData.developerProgram}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="developerProgram" className="form-check-label">
                                            Developer Program <span className="text-danger">*</span>
                                            <span
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="right"
                                                data-bs-custom-class="custom-tooltip"
                                                data-bs-title='Indicates if this API is part of the Developer Program. Checking this will reveal additional fields related to developer program requirements.'
                                                style={{ cursor: "pointer", marginLeft: "8px" }}
                                            >
                                                <Icon
                                                    name="ic_information"
                                                    size={18}
                                                    color="#777"
                                                    className="msi-icon"
                                                />
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Section */}
                    <div className="card mb-4 rounded-1">
                        <h5 className="card-title m-3 mb-0">Contact</h5>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-4">
                                    <label htmlFor="productContactEmail" className="form-label">
                                        Product Contact Email <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="email"
                                            id="productContactEmail"
                                            name="productContactEmail"
                                            className="form-control"
                                            value={formData.productContactEmail}
                                            onChange={handleChange}
                                            required
                                        />
                                        <AppTooltip
                                            title='The email address of the primary contact person for product-related inquiries about the API. This person will have Edit and Delete permissions over the APIs in the catalog.'
                                            placement="bottom"/>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <label htmlFor="technicalContactEmail" className="form-label">
                                        Technical Contact Email <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="email"
                                            id="technicalContactEmail"
                                            name="technicalContactEmail"
                                            className="form-control"
                                            value={formData.technicalContactEmail}
                                            onChange={handleChange}
                                            required
                                        />
                                        <AppTooltip
                                            title='The email address of the primary contact person for technical inquiries, support, or issues related to the API. This person will have Edit and Delete permissions over the APIs in the catalog.'
                                            placement="bottom"/>
                                    </div>
                                </div>
                                {formData.developerProgram && (
                                    <div className="col-lg-4">
                                        <label htmlFor="licensingProgramManagerContactEmail" className="form-label">
                                            Licensing Program Manager Contact Email <span className="text-danger">*</span>
                                        </label>
                                        <div className="position-relative input-group">
                                            <input
                                                type="email"
                                                id="licensingProgramManagerContactEmail"
                                                name="licensingProgramManagerContactEmail"
                                                className="form-control"
                                                value={formData.licensingProgramManagerContactEmail}
                                                onChange={handleChange}
                                                required
                                                />
                                            <AppTooltip
                                                title='The email address of the contact responsible for licensing and commercial terms related to the API.'
                                                placement="bottom"/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Other Section */}
                    <div className="card mb-4 rounded-1">
                        <h5 className="card-title m-3 mb-0">Other</h5>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-4">
                                    <label htmlFor="dependencies" className="form-label">
                                        Dependencies
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="text"
                                            id="dependencies"
                                            name="dependencies"
                                            className="form-control"
                                            value={formData.dependencies}
                                            onChange={handleChange}
                                        />
                                        <AppTooltip
                                            title='List any other APIs, services, systems, or libraries that this API relies on to function.'
                                            placement="bottom"/>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <label htmlFor="internalApiRepository" className="form-label">
                                        Internal API Repository <span className="text-danger">*</span>
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="url"
                                            id="internalApiRepository"
                                            name="internalApiRepository"
                                            className={`form-control ${internalApiRepositoryError ? 'is-invalid' : ''}`}
                                            value={formData.internalApiRepository}
                                            onChange={handleChange}
                                            required
                                        />
                                        {internalApiRepositoryError && (
                                            <div className="invalid-feedback">{internalApiRepositoryError}</div>
                                        )}
                                        <AppTooltip
                                            title='The URL to the internal source code repository for the API (e.g., GitLab, GitHub). If there is a README.md file in the root, it will be used to describe the API in AI datastore.'
                                            placement="bottom"/>
                                    </div>
                                </div>
                                {formData.developerProgram && (
                                    <div className="col-lg-4">
                                        <label htmlFor="externalApiRepository" className="form-label">
                                            External API Repository  <span className="text-danger">*</span>
                                        </label>
                                        <div className="position-relative input-group">
                                            <input
                                                type="url"
                                                id="externalApiRepository"
                                                name="externalApiRepository"
                                                className="form-control"
                                                value={formData.externalApiRepository}
                                                onChange={handleChange}
                                                required
                                            />
                                            <AppTooltip
                                                title='The URL to the publicly accessible or partner-facing source code repository, if applicable. Typically available through MSI Developer’s Programs. If there is a README.md file in the root, it will be used to describe the API in AI datastore.'
                                                placement="bottom"/>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="row mt-3">
                                <div className="col-lg-4">
                                    <label htmlFor="documentationLink" className="form-label">
                                        Documentation Link URL
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="url"
                                            id="documentationLink"
                                            name="documentationLink"
                                            className={`form-control ${documentationLinkError ? 'is-invalid' : ''}`}
                                            value={formData.documentationLink}
                                            onChange={handleChange}
                                        />
                                        {documentationLinkError && (
                                            <div className="invalid-feedback">{documentationLinkError}</div>
                                        )}
                                        <AppTooltip
                                            title="(Optional) A direct URL to the API's user documentation (e.g., Swagger UI, Postman
                                                docs, Confluence page). This url will be fetched to feed AI datastore for rich AI chatbot
                                                interaction."
                                            placement="bottom"/>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <label htmlFor="apiSpecification" className="form-label">
                                        API Specification file
                                    </label>
                                    <div className="position-relative input-group">
                                        <input
                                            type="file"
                                            accept=".json,.yaml,.yml,.xml,.wsdl"
                                            onChange={handleApiSpecFileChange}
                                            style={{ display: 'none' }}
                                            id="apiSpecFileInput"
                                        />
                                        <Button
                                            text={selectedApiSpecFile ? selectedApiSpecFile.name : "Upload API Spec"}
                                            buttonType="secondary"
                                            className="me-2"
                                            onClick={() => document.getElementById('apiSpecFileInput')?.click()}
                                        />
                                        {selectedApiSpecFile && (
                                            <span
                                                onClick={() => {
                                                    setSelectedApiSpecFile(null);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        apiSpecificationFile: ''
                                                    }));
                                                }}
                                                style={{ cursor: 'pointer', color: 'red', marginLeft: '8px', fontSize: '20px' }}
                                                title="Remove file"
                                            >
                                                ×
                                            </span>
                                        )}
                                        <span
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="bottom"
                                            data-bs-custom-class="custom-tooltip"
                                            data-bs-title="(Optional) Upload the API specification file (e.g., OpenAPI JSON/YAML, WSDL). This file will be stored as an attachment and can be used for mocking applications if applicable."
                                            style={{ cursor: "pointer" }}
                                        >
                                            <Icon
                                                name="ic_information"
                                                size={18}
                                                color="#777"
                                                className="msi-icon"
                                            />
                                        </span>
                                    </div>
                                </div>
                                {formData.developerProgram && (
                                    <div className="col-lg-4">
                                        <label htmlFor="exportControlApprovalLimitations" className="form-label">
                                            Export Control Approval/Limitations  <span className="text-danger">*</span>
                                        </label>
                                        <div className="position-relative input-group">
                                            <input
                                                type="text"
                                                id="exportControlApprovalLimitations"
                                                name="exportControlApprovalLimitations"
                                                className="form-control"
                                                value={formData.exportControlApprovalLimitations}
                                                onChange={handleChange}
                                                required
                                            />
                                            <AppTooltip
                                                title='Details regarding any export control
                                                    approvals, restrictions, or limitations applicable to this API.'
                                                placement="bottom"/>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {formData.developerProgram && (
                                <div className="row mt-3">
                                    <div className="col-lg-4">
                                        <label htmlFor="intellectualPropertyApprovalSystemNumber" className="form-label">
                                            Intellectual Property Approval System (IPAS) Number <span className="text-danger">*</span>
                                        </label>
                                        <div className="position-relative input-group">
                                            <input
                                                type="text"
                                                id="intellectualPropertyApprovalSystemNumber"
                                                name="intellectualPropertyApprovalSystemNumber"
                                                className="form-control"
                                                value={formData.intellectualPropertyApprovalSystemNumber}
                                                onChange={handleChange}
                                                required
                                            />
                                            <AppTooltip
                                                title='The reference number from the Intellectual
                                                    Property Approval System, if applicable.'
                                                placement="bottom"/>
                                        </div>
                                    </div>

                                    <div className="col-lg-4">
                                        <label htmlFor="apiDataPrivacyAssessment" className='form-label'>
                                            API Data Privacy Assessment  <span className="text-danger">*</span>
                                        </label>
                                        <div className="position-relative input-group">
                                            <select
                                                id="apiDataPrivacyAssessment"
                                                name="apiDataPrivacyAssessment"
                                                className="form-select"
                                                value={formData.apiDataPrivacyAssessment}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select</option>
                                                {sortedGovernanceOptions.apiDataPrivacyAssessments.map((v) => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                            <AppTooltip
                                                title='Indicates whether a data privacy assessment
                                                    has been conducted for the API.'
                                                placement="bottom"/>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <label htmlFor="licenseType" className='form-label'>
                                            License Type  <span className="text-danger">*</span>
                                        </label>
                                        <div className="position-relative input-group">
                                            <select
                                                id="licenseType"
                                                name="licenseType"
                                                className="form-select"
                                                value={formData.licenseType}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select</option>
                                                {sortedGovernanceOptions.licenseTypes.map((v) => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                            <AppTooltip
                                                title='The type of license under which the API is made available.'
                                                placement="bottom"/>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddOrEditApi;